import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useBuyPlans } from "../useHook";
import ModalAddBuyPlan from "../../../Components/Modals/BuyPlans/BuyPlansModal";
import { MdDelete, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { IoEye } from "react-icons/io5";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

function BuyPlans() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.StandardPlanPricing.CREATE);
  const canUpdate = hasPermission(SystemPermissions.StandardPlanPricing.UPDATE);
  const canDelete = hasPermission(SystemPermissions.StandardPlanPricing.DELETE);
  const canRead = hasPermission(SystemPermissions.StandardPlanPricing.READ);

  const { createPlan, deletePlan, getPlans, updatePlan } = useBuyPlans();
  const [plansData, setPlansData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const fetchPlans = async () => {
    await getPlans(setPlansData);
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setEditPlan(null);
  };
  const handlePageView = (buyPlanId) => {
    navigate("/buy-this-plan/details", { state: { buyPlanId } });
  };
  const handleAddOrUpdatePlan = async (planData, planId = null) => {
    try {
      if (planId) {
        const response = await updatePlan(planId, planData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Plan Updated",
            text: "Plan has been updated successfully!",
          });
        }
      } else {
        const response = await createPlan(planData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Plan Added",
            text: "Plan has been added successfully!",
          });
        }
      }
      await fetchPlans();
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "An error occurred. Please try again.",
      });
    }
    closeModal();
  };

  const handleEditPlan = (plan) => {
    setEditPlan(plan);
    openModal();
  };

  const handleDeletePlan = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deletePlan(id);
        if (response) {
          Swal.fire("Deleted!", "The plan has been deleted.", "success");
          await fetchPlans();
        }
      } catch {
        Swal.fire("Error!", "Failed to delete the plan.", "error");
      }
    }
  };

  const showActions = canRead || canUpdate || canDelete;

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Manage Buy Plans</h2>

        {canCreate && (
          <button onClick={openModal} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add Plan
          </button>
        )}
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow my-5">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No</th>
                <th className="table-th">Name</th>
                <th className="table-th">Up To 5 Marla</th>
                <th className="table-th">From 5 to 8 Marla</th>
                <th className="table-th">From 8 to 12 Marla</th>
                <th className="table-th">From 12 to 18 Marla</th>
                <th className="table-th">From 18 to 25 Marla</th>
                <th className="table-th">Above 25 Marla</th>
                {showActions && <th className="table-th">Action</th>}
              </tr>
            </thead>
            <tbody>
              {plansData?.map((plan, index) => (
                <tr
                  key={plan.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                  } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="table-td">{index + 1}</td>
                  <td className="table-td">{plan.name}</td>
                  <td className="table-td">{plan.upto_5_marla}</td>
                  <td className="table-td">{plan.from_5_to_12_marla}</td>
                  <td className="table-td">{plan.from_8_to_12_marla}</td>
                  <td className="table-td">{plan.from_12_to_18_marla}</td>
                  <td className="table-td">{plan.from_18_to_25_marla}</td>
                  <td className="table-td">{plan.above_25_marla}</td>

                  {showActions && (
                    <td className="table-td flex space-x-2">
                      {canRead && (
                        <button
                          onClick={() => handlePageView(plan.id)}
                          className="btn btn-view"
                        >
                          <IoEye className="text-white" /> View
                        </button>
                      )}
                      {canUpdate && (
                        <button
                          className="btn btn-edit text-white"
                          onClick={() => handleEditPlan(plan)}
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn btn-delete text-white"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <MdDelete className="text-white" /> Delete
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ModalAddBuyPlan
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleAddOrUpdatePlan}
        editPlan={editPlan}
      />
    </div>
  );
}

export default BuyPlans;
